// BUILD-046 task 8.25 (supporting model): which action controls a row offers to the Caller (design "Oversight actions"; Req 4.12, 4.20, 4.21,
// 4.30, 4.33, 4.35, 4.38, 4.39, 7.6, 7.13, 7.14, 14.30; Permission Policy acting rules, RECORDED items 3 and 11, Decision D-J).
//
// A pure function from the Caller summary (`GET /work/me`) and the row's fields to the set of offered controls. It REFLECTS the recorded acting
// rules so the screen does not offer what the server will refuse; the server stays authoritative (every refusal is shown with its message and
// changes nothing on screen, see taskActionsFlow.ts).
//
// The controls:
//   assign-to        "Assign to…"            an UNCLAIMED Shared_Task: assign it to someone who can take it from the queue
//   recovery-assign  "Recovery assignment…"  an UNCLAIMED Shared_Task: an explicit assignment that adds no one to the queue (Req 4.33, 4.34)
//   reassign         "Reassign…"             a CLAIMED open task
//   return-to-queue  "Return to queue"       a CLAIMED open Shared_Task
//   reopen           "Reopen"                a COMPLETED ordinary task (never a Source_Governed one: Req 8.16, D-O)
// Done and complete-on-behalf are NOT controls of this model and are never offered to oversight (Req 8.13). Nothing here edits another
// person's notes. Nothing is automatic.
//
// CHANGED 2026-10-22 (BUILD-046 G1, Builder decision requester-edit-authority): the owner basis no longer offers Reassign… (being the assignee alone grants no reassignment on the server); it offers Return to queue only,
// which undoes the owner's own claim. Reassign… is the overseer's.
//
// CHANGED 2026-10-23 (BUILD-046 G2, Builder decision requester-edit-authority, continuation 958e5394): the person who ISSUED a task controls its assignment within the supported modes. On the requester's issued list
// (`view === 'issued'`, the row's `requesterEmployeeId` is the viewer) a Single or queue task offers Reassign… once claimed (and Return to queue for a claimed Shared_Task), and Assign to… while an unclaimed
// Shared_Task still has people in its queue. The requester is never offered the Recovery assignment (an overseer's), Reopen, or any control on an Everyone request. This is the requester's own authority, not
// an oversight scope; the server (canReassign) decides every request, including the source authority of a source-owned task, and a row shown in its restricted form (`redacted`) offers nothing.
//
// Who is offered what:
//   requester basis: the viewer issued the task (see above), on the issued list only;
//   overseer basis : an Authorized_Overseer (`canOversee`, or `companyOversight` for the Company_Oversight_Scope holder), on a row of the
//                    oversight list (the server returned only rows in the overseer's scope) - or, on the requester's issued list, only the
//                    company holder, whose scope is known to be the whole company;
//   owner basis    : the owner of a claimed task (`ownerEmployeeId` is the viewer), while eligible. A row the viewer can no longer open the
//                    source of (`redacted`) means the owner is no longer an Eligible_Employee (Req 4.39a), so no owner-basis control;
//   never          : a Work_Group_Administrator by that authority alone (`canAdministerGroups` is not an input to any decision here),
//                    the admin group or PM role, a mere assignee or collaborator, an unrelated Employee.
// An Everyone assignment is not reassigned or returned (its owner is a member of a fixed set; the set actions of task 8.23 apply), a
// cancelled task has no controls, and a task whose restricted source the viewer cannot open offers overseers the same controls with no
// restricted text (Req 14.30). A manager's scope is the server's: a manager is offered controls only on the oversight list, where presence
// in the list is the server's statement that the row is in scope.
//
// Pure module: no React, no I/O, no runtime imports. Erasable TypeScript only (run by `node --experimental-strip-types`).

export type ActionControl = 'assign-to' | 'recovery-assign' | 'reassign' | 'return-to-queue' | 'reopen';

/** Every control this model can ever offer, in display order. There is no Done, complete-on-behalf or edit-notes control. */
export const ACTION_CONTROLS: readonly ActionControl[] = Object.freeze(['assign-to', 'recovery-assign', 'reassign', 'return-to-queue', 'reopen'] as ActionControl[]);

/** Where the row is shown: the oversight list, the viewer's own tasks, the requester's issued list, or a view with no oversight controls. */
export type ActionView = 'oversight' | 'my-tasks' | 'issued' | 'shared' | 'team';

export interface ActionMe {
  employee?: { employeeId?: string } | null;
  canOversee?: boolean;
  companyOversight?: boolean;
  /** Recorded for completeness: it grants NO control here (Req 4.30, 4.39, 14.20). */
  canAdministerGroups?: boolean;
}

export interface ActionRow {
  status?: string;
  assignmentKind?: string;
  unclaimed?: boolean;
  ownerEmployeeId?: string;
  sourceGoverned?: boolean;
  reassignmentNeeded?: boolean;
  redacted?: boolean;
  lifecycle?: string;
  /** Assigned by Me only: this task is also on the viewer's own oversight list (GET /work/oversight returned it, so it is in the viewer's scope). The requester role alone never sets it. */
  inOversightScope?: boolean;
  /** The Employee who issued the task (GET /work rows). On the issued list, a viewer who is this Employee has the requester basis. */
  requesterEmployeeId?: string;
}

export type ActionBasis = 'overseer' | 'owner' | 'requester';

export interface ActionDecision {
  controls: ActionControl[];
  /** Why any control is offered. Empty when none is. */
  bases: ActionBasis[];
}

const OPEN_STATUSES: readonly string[] = ['active', 'in_progress', 'waiting', 'blocked'];

const NONE: ActionDecision = Object.freeze({ controls: [] as ActionControl[], bases: [] as ActionBasis[] }) as ActionDecision;

function viewerId(me: ActionMe | null | undefined): string {
  const id = me?.employee?.employeeId;
  return typeof id === 'string' ? id : '';
}

/** An Authorized_Overseer as the Caller summary states it. A group administrator alone is not one. */
export function isOverseer(me: ActionMe | null | undefined): boolean {
  return me?.canOversee === true || me?.companyOversight === true;
}

function ownerOf(row: ActionRow): string {
  return typeof row.ownerEmployeeId === 'string' ? row.ownerEmployeeId : '';
}

export function offeredControls(me: ActionMe | null | undefined, row: ActionRow | null | undefined, view: ActionView): ActionDecision {
  const viewer = viewerId(me);
  if (viewer === '' || row === null || row === undefined) return NONE;
  if (view === 'shared' || view === 'team') return NONE;
  if (row.lifecycle === 'cancelled') return NONE;

  const owner = ownerOf(row);
  const claimed = owner !== '';
  const unclaimed = !claimed && row.unclaimed === true;
  if (!claimed && !unclaimed) return NONE; // a row that says neither is not guessed at
  const kind = typeof row.assignmentKind === 'string' ? row.assignmentKind : '';
  const open = typeof row.status === 'string' && OPEN_STATUSES.includes(row.status);
  const completed = row.status === 'completed';

  const overseerBasis = view === 'oversight' ? isOverseer(me) : view === 'issued' ? me?.companyOversight === true || (isOverseer(me) && row.inOversightScope === true) : false;
  const ownerBasis = claimed && owner === viewer && row.redacted !== true;
  // The requester basis (G2): the viewer issued this task and sees it in full. It is offered on the requester's issued list only.
  const requesterBasis = view === 'issued' && typeof row.requesterEmployeeId === 'string' && row.requesterEmployeeId === viewer && row.redacted !== true;
  if (!overseerBasis && !ownerBasis && !requesterBasis) return NONE;

  const controls: ActionControl[] = [];
  if (unclaimed) {
    // An unclaimed Shared_Task is acted on by an overseer and by its requester (Req 4.12, 4.33; G2). With no Queue_Eligible_Employee left there is nobody to assign to
    // from the queue, so only the overseer's recovery assignment is offered (Req 4.38); the requester has none.
    if ((overseerBasis || requesterBasis) && open && (kind === '' || kind === 'shared')) {
      if (row.reassignmentNeeded !== true) controls.push('assign-to');
      if (overseerBasis) controls.push('recovery-assign');
    }
  } else if (open && kind !== 'everyone') {
    // On the owner's own My tasks row only a claimed Shared_Task gets controls: an ordinary task keeps its existing Hand off.
    if (view !== 'my-tasks' || kind === 'shared') {
      // CHANGED 2026-10-22 (BUILD-046 G1, Builder decision requester-edit-authority): being the assignee alone grants no reassignment (the server's canReassign), so Reassign is the overseer's. The claimant of a
      // Shared_Task keeps Return to queue: it undoes their own claim and names nobody new.
      if (overseerBasis || requesterBasis) controls.push('reassign');
      if (kind === 'shared') controls.push('return-to-queue');
    }
  } else if (completed && row.sourceGoverned !== true && row.redacted !== true) {
    // The owner's own My tasks row keeps its existing Restore.
    if (overseerBasis || (ownerBasis && view !== 'my-tasks')) controls.push('reopen');
  }

  if (controls.length === 0) return NONE;
  const bases: ActionBasis[] = [];
  if (overseerBasis) bases.push('overseer');
  if (ownerBasis) bases.push('owner');
  if (requesterBasis && controls.some((control) => control === 'assign-to' || control === 'reassign' || control === 'return-to-queue')) bases.push('requester');
  return { controls, bases };
}

// ───────────────────────── what each control says ─────────────────────────

export interface ControlText {
  label: string;
  /** True when the control asks who to assign to before it sends anything. */
  needsTarget: boolean;
  /** The sentence shown with the picker or the confirmation. */
  prompt: string;
  /** The button that sends it. */
  confirmLabel: string;
}

export const CONTROL_TEXT: Readonly<Record<ActionControl, ControlText>> = Object.freeze({
  'assign-to': {
    label: 'Assign to…',
    needsTarget: true,
    prompt: 'Assign this unclaimed task to someone who can take it from the queue.',
    confirmLabel: 'Assign',
  },
  'recovery-assign': {
    label: 'Recovery assignment…',
    needsTarget: true,
    prompt: 'Use this when no one in the queue can take the task. It is recorded as an explicit assignment and does not add the person to the queue.',
    confirmLabel: 'Assign',
  },
  reassign: {
    label: 'Reassign…',
    needsTarget: true,
    prompt: 'Give this task to someone else. The server checks that the person is eligible and refuses anyone who is not.',
    confirmLabel: 'Reassign',
  },
  'return-to-queue': {
    label: 'Return to queue',
    needsTarget: false,
    prompt: 'Return this task to the queue? It stops being anyone\'s task and the people in the queue can take it again.',
    confirmLabel: 'Return to queue',
  },
  reopen: {
    label: 'Reopen',
    needsTarget: false,
    prompt: 'Reopen this completed task so its owner can work on it again?',
    confirmLabel: 'Reopen',
  },
});

export function describeControl(control: ActionControl): ControlText {
  return CONTROL_TEXT[control];
}
